import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { SITE, waLink, waMessageFor } from "@/config/site";
import { trackWhatsappClick } from "@/lib/analytics";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/blog", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isHomeHero = pathname === "/" && !scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-ocean/95 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-heading font-bold text-xl md:text-2xl tracking-tight text-white flex items-center gap-2"
        >
          <span className="w-2 h-2 bg-sunset rounded-full" />
          Deep Dive Trips
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] font-medium uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-sunset" : "text-white/80 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <a
          href={waLink(waMessageFor())}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-whatsapp-cta"
          onClick={() => trackWhatsappClick("navbar")}
          className="hidden lg:inline-flex items-center gap-2 bg-sunset hover:bg-sunset-hover text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors"
        >
          <MessageCircle size={16} strokeWidth={2} /> Plan on WhatsApp
        </a>

        <button
          type="button"
          className="lg:hidden text-white p-2"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ocean-dark border-t border-white/10">
          <nav className="container-x py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 border-b border-white/10 text-sm uppercase tracking-widest ${
                    isActive ? "text-sunset" : "text-white/85"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={waLink(waMessageFor())}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-whatsapp"
              onClick={() => trackWhatsappClick("mobile-navbar")}
              className="mt-4 btn-primary"
            >
              <MessageCircle size={16} /> Plan on WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
